import {
  ActionIcon,
  AppShell,
  Box,
  Burger,
  Center,
  NavLink as MantineNavLink,
  Menu,
  ScrollArea,
  Stack,
} from "@mantine/core";
import {
  IconBrandGoogleAnalytics,
  IconBriefcase,
  IconCalendar,
  IconCurrencyKroneSwedish,
  IconHome,
  IconQuestionMark,
  IconShield,
  IconTable,
  IconUpload,
  IconUsers,
} from "@tabler/icons-react";
import { Link, useRouter } from "@tanstack/react-router";
import dayjs from "dayjs";
import { type ReactNode, useEffect } from "react";
import { NavIcon } from "~/components/custom-links/nav-icon";
import { NavLink } from "~/components/custom-links/nav-link";
import { ProfileSection } from "~/components/profile-section";
import { authClient } from "~/lib/auth-client";
import { useDesktopSidebar } from "~/stores/ui";

const nestedMenuWidth = 200;

type NavItemType = {
  label: string;
  icon: ReactNode;
  requiredRoles?: string[];
  devOnly?: boolean; // when true, the link will only be shown in development mode
} & (
    | { path: string; nestedLinks?: undefined; params?: Record<string, unknown> }
    | { path: null; nestedLinks: NavItemType[] }
  );

const links: NavItemType[] = [
  { label: "Home", path: "/", icon: <IconHome /> },
  { label: "Deals", path: "/deals", icon: <IconBriefcase /> },
  { label: "Conversions", path: "/conversions", icon: <IconBrandGoogleAnalytics />, devOnly: false },
  { label: "Customers", path: "/customers", icon: <IconUsers /> },
  { label: "Scheduler", path: `/scheduler/${dayjs().format("YYYY-MM-DD")}`, icon: <IconCalendar /> },
  {
    label: "Admin",
    path: null,
    icon: <IconShield />,
    requiredRoles: ["super_admin"],
    nestedLinks: [
      { label: "Tables", path: "/admin/tables", icon: <IconTable /> },
      { label: "Users", path: "/admin/users", icon: <IconUsers /> },
    ],
  },
  {
    label: "Accounting",
    path: null,
    icon: <IconCurrencyKroneSwedish />,
    nestedLinks: [
      { label: "Upload documents", path: "/accounting/upload-incoming-skv-documents", icon: <IconUpload /> },
      { label: "RUT Questions", path: "/accounting/rut-questions", icon: <IconQuestionMark /> },
    ],
  },
] as const;

export function DesktopLayout(props: { children: ReactNode }) {
  const isExpanded = useDesktopSidebar((state) => state.isExpanded);
  const toggleExpanded = useDesktopSidebar((state) => state.toggleExpanded);
  const session = authClient.useSession();

  const router = useRouter();

  useEffect(() => {
    if (!session.isPending && !session.data?.user && router.state.location.pathname !== "/login") {
      router.navigate({ to: "/login" });
    }
  }, [session.data?.user, session.isPending, router]);

  if (!session.data?.user) {
    return <>{props.children}</>;
  }

  return (
    <AppShell
      navbar={{
        width: isExpanded ? 300 : 48,
        breakpoint: 0,
        // collapsed: { desktop: true },
      }}>
      <AppShell.Navbar>
        <AppShell.Section grow>
          <Box pl={8}>
            <Burger opened={isExpanded} onClick={toggleExpanded} size="sm" />
          </Box>
          <NavLinks />
        </AppShell.Section>
        <AppShell.Section>
          <ProfileSection />
        </AppShell.Section>
      </AppShell.Navbar>
      <AppShell.Main>
        <Box component="main">{props.children}</Box>
      </AppShell.Main>
    </AppShell>
  );
}

function NavItem({ item }: { item: NavItemType }) {
  const session = authClient.useSession();
  const isExpanded = useDesktopSidebar((state) => state.isExpanded);
  const role = session.data?.user.role;

  if (item.devOnly && import.meta.env.PROD) {
    return null;
  }


  if (item.requiredRoles) {
    if (!role) return null;
    const roles = role.split(",");
    if (!item.requiredRoles.some((r) => roles.includes(r))) {
      return null;
    }
  }

  if (isExpanded) return <ExpandedNavItem item={item} />;
  return <CollapsedNavItem item={item} />;
}

function ExpandedNavItem({ item }: { item: NavItemType }) {
  if (!item.nestedLinks) {
    return <NavLink label={item.label} to={item.path} leftSection={item.icon} params={item.params} />;
  }

  return (
    <MantineNavLink leftSection={item.icon} label={item.label} my={-2}>
      {item.nestedLinks.map((nestedItem) => (
        <NavItem key={nestedItem.path} item={nestedItem} />
      ))}
    </MantineNavLink>
  );
}

function CollapsedNavItem({ item }: { item: NavItemType }) {
  if (!item.nestedLinks) {
    return <NavIcon label={item.label} to={item.path} icon={item.icon} params={item.params} />;
  }

  return (
    <Menu shadow="md" width={200} position="right-start" withArrow trigger="hover">
      <Menu.Target>
        <ActionIcon color="dark" variant="transparent">
          {item.icon}
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>{item.label}</Menu.Label>
        {item.nestedLinks.map((nestedItem) => {
          if (!nestedItem.nestedLinks) {
            return (
              <Menu.Item key={nestedItem.path} leftSection={nestedItem.icon} component={Link} to={nestedItem.path}>
                {nestedItem.label}
              </Menu.Item>
            );
          }
          return <NavIconWithSubMenu key={nestedItem.path} link={nestedItem} />;
        })}
      </Menu.Dropdown>
    </Menu>
  );
}

function NavIconWithSubMenu({ link }: { link: NavItemType }) {
  if (!link.nestedLinks) return null;
  return (
    <Menu.Sub width={nestedMenuWidth}>
      <Menu.Sub.Target>
        <Menu.Sub.Item leftSection={link.icon}>{link.label}</Menu.Sub.Item>
      </Menu.Sub.Target>

      <Menu.Sub.Dropdown>
        {link.nestedLinks.map((link) => {
          if (link.nestedLinks) {
            return <NavIconWithSubMenu key={link.path} link={link} />;
          }
          return (
            <Menu.Item key={link.path} component={Link} to={link.path || "#"} leftSection={link.icon}>
              {link.label}
            </Menu.Item>
          );
        })}
      </Menu.Sub.Dropdown>
    </Menu.Sub>
  );
}

function NavLinks() {
  const isExpanded = useDesktopSidebar((state) => state.isExpanded);

  if (isExpanded) return <ExpandedNavLinks />;
  return <CollapsedNavLinks />;
}

function ExpandedNavLinks() {
  return (
    <ScrollArea h="calc(100dvh - 100px)">
      <Stack gap={3}>
        {links.map((link) => (
          <NavItem key={link.label} item={link} />
        ))}
        {/* <CrmNavLinks /> */}
      </Stack>
    </ScrollArea>
  );
}

function CollapsedNavLinks() {
  return (
    <ScrollArea h="calc(100dvh - 100px)" mt={4}>
      <Stack gap={12}>
        {links.map((link) => (
          <Center key={link.label}>
            <NavItem item={link} />
          </Center>
        ))}
        {/* <CrmNavLinks /> */}
      </Stack>
    </ScrollArea>
  );
}
